import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Database, Plus, Trash2, Edit2, Check, X,
  Globe, Key, Activity, Save, Link, Star, Shield, AlertTriangle,
  ToggleLeft, ToggleRight, Flag
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';

const SourcesPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [sourcesByCategory, setSourcesByCategory] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    source_type: 'website',
    weight: 0.5,
    requires_auth: false,
    credentials: { username: '', password: '', api_key: '' },
    description: ''
  });

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchSources();
    fetchSourcesByCategory();
  }, []);

  const fetchSources = async () => {
    try {
      const response = await axios.get(`${API}/sources`, authHeaders);
      setSources(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des sources');
    }
  };

  const fetchSourcesByCategory = async () => {
    try {
      const response = await axios.get(`${API}/sources/by-category`, authHeaders);
      setSourcesByCategory(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const toggleSource = async (sourceId) => {
    try {
      await axios.put(`${API}/sources/${sourceId}/toggle`, {}, authHeaders);
      fetchSources();
      fetchSourcesByCategory();
      toast.success('Source mise à jour');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const toggleCategory = async (category, enabled) => {
    try {
      await axios.put(`${API}/sources/bulk-toggle`, { category, enabled }, authHeaders);
      fetchSources();
      fetchSourcesByCategory();
      toast.success(`Sources ${enabled ? 'activées' : 'désactivées'}`);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      credentials: formData.requires_auth ? formData.credentials : null
    };

    try {
      if (editingSource) {
        await axios.put(`${API}/sources/${editingSource.id}`, payload, authHeaders);
        toast.success('Source mise à jour');
      } else {
        await axios.post(`${API}/sources`, payload, authHeaders);
        toast.success('Source ajoutée');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchSources();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette source ?')) return;
    
    try {
      await axios.delete(`${API}/sources/${id}`, authHeaders);
      toast.success('Source supprimée');
      fetchSources();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleSourceActive = async (source) => {
    try {
      await axios.put(`${API}/sources/${source.id}`, { is_active: !source.is_active }, authHeaders);
      fetchSources();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const openEditDialog = (source) => {
    setEditingSource(source);
    setFormData({
      name: source.name,
      url: source.url,
      source_type: source.source_type,
      weight: source.weight,
      requires_auth: source.requires_auth,
      credentials: { username: '', password: '', api_key: '' },
      description: source.description || ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingSource(null);
    setFormData({
      name: '',
      url: '',
      source_type: 'website',
      weight: 0.5,
      requires_auth: false,
      credentials: { username: '', password: '', api_key: '' },
      description: ''
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'api': return <Activity className="w-4 h-4 text-marine-green" />;
      case 'rss': return <Star className="w-4 h-4 text-marine-amber" />;
      default: return <Globe className="w-4 h-4 text-marine-cyan" />;
    }
  };

  const getWeightColor = (weight) => {
    if (weight >= 0.8) return 'text-marine-green';
    if (weight >= 0.5) return 'text-marine-amber';
    return 'text-marine-red';
  };

  return (
    <div className="min-h-screen bg-marine-void" data-testid="sources-page">
      {/* Header */}
      <header className="border-b border-marine-slate/50 bg-marine-card/50 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 text-marine-slate hover:text-marine-cyan transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-heading text-marine-cyan text-lg tracking-widest">
                GESTION DES SOURCES
              </h1>
              <p className="font-mono text-white/70 text-xs">
                Configuration des sources OSINT
              </p>
            </div>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button
                onClick={resetForm}
                className="flex items-center gap-2 px-4 py-2 bg-marine-cyan/10 border border-marine-cyan text-marine-cyan hover:bg-marine-cyan hover:text-black transition-all"
                data-testid="add-source-btn"
              >
                <Plus className="w-4 h-4" />
                <span className="font-heading text-xs uppercase tracking-widest">Ajouter Source</span>
              </button>
            </DialogTrigger>
            
            <DialogContent className="bg-marine-card border border-marine-slate max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-marine-cyan tracking-widest">
                  {editingSource ? 'MODIFIER SOURCE' : 'NOUVELLE SOURCE'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <label className="block font-mono text-white/70 text-xs uppercase tracking-wider mb-2">
                    Nom de la source
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-black/50 border border-marine-slate/50 text-marine-text font-mono text-sm py-2 px-3 focus:border-marine-cyan focus:outline-none"
                    placeholder="ex: IEA Energy Data"
                    required
                    data-testid="source-name-input"
                  />
                </div>

                <div>
                  <label className="block font-mono text-white/70 text-xs uppercase tracking-wider mb-2">
                    URL
                  </label>
                  <div className="relative">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-marine-slate" />
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      className="w-full bg-black/50 border border-marine-slate/50 text-marine-text font-mono text-sm py-2 pl-10 pr-3 focus:border-marine-cyan focus:outline-none"
                      placeholder="https://..."
                      required
                      data-testid="source-url-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-white/70 text-xs uppercase tracking-wider mb-2">
                      Type
                    </label>
                    <select
                      value={formData.source_type}
                      onChange={(e) => setFormData({ ...formData, source_type: e.target.value })}
                      className="w-full bg-black/50 border border-marine-slate/50 text-marine-text font-mono text-sm py-2 px-3 focus:border-marine-cyan focus:outline-none"
                      data-testid="source-type-select"
                    >
                      <option value="website">Site Web</option>
                      <option value="api">API</option>
                      <option value="rss">RSS Feed</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-mono text-white/70 text-xs uppercase tracking-wider mb-2">
                      Pondération: {(formData.weight * 100).toFixed(0)}%
                    </label>
                    <Slider
                      value={[formData.weight * 100]}
                      onValueChange={(value) => setFormData({ ...formData, weight: value[0] / 100 })}
                      max={100}
                      step={5}
                      className="mt-3"
                      data-testid="source-weight-slider"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-white/70 text-xs uppercase tracking-wider mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full bg-black/50 border border-marine-slate/50 text-marine-text font-mono text-sm py-2 px-3 focus:border-marine-cyan focus:outline-none resize-none h-20"
                    placeholder="Description de la source..."
                    data-testid="source-description-input"
                  />
                </div>

                {/* Credentials Section */}
                <div className="border border-marine-slate/30 p-3">
                  <label className="flex items-center gap-2 cursor-pointer mb-3">
                    <input
                      type="checkbox"
                      checked={formData.requires_auth}
                      onChange={(e) => setFormData({ ...formData, requires_auth: e.target.checked })}
                      className="w-4 h-4 bg-marine-void border-marine-slate"
                      data-testid="requires-auth-checkbox"
                    />
                    <Key className="w-4 h-4 text-marine-amber" />
                    <span className="font-mono text-marine-text text-xs uppercase">Requiert authentification</span>
                  </label>

                  {formData.requires_auth && (
                    <div className="space-y-3 pt-2 border-t border-marine-slate/30">
                      <div>
                        <label className="block font-mono text-white/70 text-xs mb-1">Nom d'utilisateur</label>
                        <input
                          type="text"
                          value={formData.credentials.username}
                          onChange={(e) => setFormData({
                            ...formData,
                            credentials: { ...formData.credentials, username: e.target.value }
                          })}
                          className="w-full bg-black/50 border border-marine-slate/50 text-marine-text font-mono text-sm py-2 px-3 focus:border-marine-cyan focus:outline-none"
                          data-testid="cred-username-input"
                        />
                      </div>
                      <div>
                        <label className="block font-mono text-white/70 text-xs mb-1">Mot de passe</label>
                        <input
                          type="password"
                          value={formData.credentials.password}
                          onChange={(e) => setFormData({
                            ...formData,
                            credentials: { ...formData.credentials, password: e.target.value }
                          })}
                          className="w-full bg-black/50 border border-marine-slate/50 text-marine-text font-mono text-sm py-2 px-3 focus:border-marine-cyan focus:outline-none"
                          data-testid="cred-password-input"
                        />
                      </div>
                      <div>
                        <label className="block font-mono text-white/70 text-xs mb-1">Clé API</label>
                        <input
                          type="password"
                          value={formData.credentials.api_key}
                          onChange={(e) => setFormData({
                            ...formData,
                            credentials: { ...formData.credentials, api_key: e.target.value }
                          })}
                          className="w-full bg-black/50 border border-marine-slate/50 text-marine-text font-mono text-sm py-2 px-3 focus:border-marine-cyan focus:outline-none"
                          data-testid="cred-apikey-input"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 py-2 border border-marine-slate text-marine-slate hover:border-marine-red hover:text-marine-red transition-colors font-mono text-xs uppercase tracking-wider"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 bg-marine-cyan/10 border border-marine-cyan text-marine-cyan hover:bg-marine-cyan hover:text-black transition-all font-mono text-xs uppercase tracking-wider flex items-center justify-center gap-2"
                    data-testid="save-source-btn"
                  >
                    <Save className="w-4 h-4" />
                    Enregistrer
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Sources List by Category */}
      <main className="p-4 md:p-6 space-y-6">
        {/* Category summaries */}
        <div className="grid md:grid-cols-3 gap-4">
          {Object.entries(sourcesByCategory).map(([key, cat]) => {
            const enabledCount = cat.sources?.filter(s => s.enabled).length || 0;
            const totalCount = cat.sources?.length || 0;
            const categoryColors = {
              western: { border: 'border-marine-green', bg: 'bg-marine-green/10', text: 'text-marine-green', icon: Shield },
              adversary: { border: 'border-marine-red', bg: 'bg-marine-red/10', text: 'text-marine-red', icon: AlertTriangle },
              regional: { border: 'border-marine-amber', bg: 'bg-marine-amber/10', text: 'text-marine-amber', icon: Globe },
            };
            const style = categoryColors[key] || categoryColors.western;
            const IconComp = style.icon;
            
            return (
              <div key={key} className={`${style.bg} border ${style.border} p-4`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <IconComp className={`w-5 h-5 ${style.text}`} />
                    <span className={`font-mono text-sm uppercase ${style.text}`}>{cat.name}</span>
                  </div>
                  <span className="font-mono text-white/70 text-xs">{enabledCount}/{totalCount}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleCategory(key, true)}
                    className="flex-1 py-2 bg-marine-green/20 border border-marine-green text-marine-green hover:bg-marine-green hover:text-black font-mono text-xs"
                  >
                    Tout activer
                  </button>
                  <button
                    onClick={() => toggleCategory(key, false)}
                    className="flex-1 py-2 bg-marine-red/20 border border-marine-red text-marine-red hover:bg-marine-red hover:text-black font-mono text-xs"
                  >
                    Tout désactiver
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detailed sources by category */}
        {Object.entries(sourcesByCategory).map(([key, cat]) => {
          const categoryColors = {
            western: { border: 'border-marine-green/50', header: 'bg-marine-green/20', text: 'text-marine-green' },
            adversary: { border: 'border-marine-red/50', header: 'bg-marine-red/20', text: 'text-marine-red' },
            regional: { border: 'border-marine-amber/50', header: 'bg-marine-amber/20', text: 'text-marine-amber' },
          };
          const style = categoryColors[key] || categoryColors.western;
          
          return (
            <div key={key} className={`bg-marine-card border ${style.border}`}>
              <div className={`${style.header} px-4 py-3 border-b ${style.border}`}>
                <span className={`font-mono ${style.text} text-sm uppercase tracking-wider`}>
                  {cat.name} ({cat.sources?.filter(s => s.enabled).length || 0} activées)
                </span>
              </div>
              
              <div className="divide-y divide-marine-slate/20">
                {cat.sources?.map((source) => (
                  <div 
                    key={source.id}
                    className={`p-4 flex items-center justify-between ${source.enabled ? '' : 'opacity-50'}`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <button
                        onClick={() => toggleSource(source.id)}
                        className={`w-10 h-6 rounded-full relative transition-colors ${
                          source.enabled ? 'bg-marine-green' : 'bg-marine-slate/50'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                          source.enabled ? 'right-1' : 'left-1'
                        }`} />
                      </button>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(source.source_type)}
                          <span className="font-mono text-white text-sm">{source.name}</span>
                          <span className="font-mono text-white/50 text-xs px-2 py-0.5 bg-marine-slate/30 rounded">
                            {source.language?.toUpperCase() || 'EN'}
                          </span>
                          <span className="font-mono text-white/50 text-xs">
                            {source.country}
                          </span>
                        </div>
                        <p className="font-mono text-white/50 text-xs mt-1 truncate max-w-md">
                          {source.url}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className={`font-heading text-lg ${getWeightColor(source.weight)}`}>
                          {(source.weight * 100).toFixed(0)}%
                        </p>
                        <p className="font-mono text-white/50 text-xs">fiabilité</p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openEditDialog(source)}
                          className="p-2 text-marine-slate hover:text-marine-cyan"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(source.id)}
                          className="p-2 text-marine-slate hover:text-marine-red"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </main>
    </div>
  );
};

export default SourcesPage;
